/**
 * ILO rebuild trigger — the dashboard's one write into the clustering
 * pipeline. Backs the GenerationPicker's "New clustering pass" form:
 * an admin launches a fresh clustering generation (optionally *steered*
 * toward named attributes) and pins it once it completes.
 *
 * Steering (null_merge only): the operator supplies probe texts —
 * natural-language attribute descriptions — and each becomes a
 * consolidated root in the resulting hierarchy. Probes ride in the
 * `hyperparameters_json` blob the ILO worker parses; every other
 * algorithm ignores them, so we only attach them for a steerable one.
 *
 * Enum encoding follows the dashboard-wide rule: proto enums go on the
 * wire as their full constant-name string (grpc-gateway JSON), NOT the
 * short lowercase form the analytics graph returns. See the analytics
 * list in GenerationPicker.jsx for the read-side short names.
 */
import { restPost } from "./restClient.js";

const REBUILD_PATH = "/v1/ilo/rebuilds";

// Default confidence cut for the centroid-confidence attribute claim (matches
// the ILO worker's own default). A fine cell is claimed by a taxonomy node only
// when its uniqueness margin over sibling nodes clears this; higher = tighter,
// cleaner domains, fewer claimed. Clamped so an out-of-range field never ships.
export const DEFAULT_CONF_CUT = 2.0;
const CONF_CUT_MIN = 0.5;
const CONF_CUT_MAX = 5.0;

// This catalog mirrors api.proto so the picker sends the same algorithm token
// that rebuild history and analytics use.
// short lowercase name — same token the analytics generation list and
// the filter chips use, so the compose select and the browse list
// speak one vocabulary. `proto` is what goes on the wire.
export const ALGORITHMS = [
    { value: "null_merge",  proto: "CLUSTERING_ALGORITHM_NULL_MERGE",  label: "null_merge (steerable)", steerable: true },
    { value: "bisecting",    proto: "CLUSTERING_ALGORITHM_BISECTING",   label: "Bisecting K-means" },
    { value: "leiden_hier", proto: "CLUSTERING_ALGORITHM_LEIDEN_HIER", label: "leiden_hier" },
    { value: "leiden",      proto: "CLUSTERING_ALGORITHM_LEIDEN",      label: "leiden" },
    { value: "hdbscan",     proto: "CLUSTERING_ALGORITHM_HDBSCAN",     label: "hdbscan" },
    { value: "kmeans",      proto: "CLUSTERING_ALGORITHM_KMEANS",      label: "kmeans" },
    { value: "gmm",         proto: "CLUSTERING_ALGORITHM_GMM",         label: "gmm" },
    { value: "iforest_gmm", proto: "CLUSTERING_ALGORITHM_IFOREST_GMM", label: "iforest_gmm" },
];

const BY_VALUE = new Map(ALGORITHMS.map((a) => [a.value, a]));

export function algorithmMeta(value) {
    return BY_VALUE.get(value) ?? null;
}

export function isSteerable(value) {
    return Boolean(BY_VALUE.get(value)?.steerable);
}

// Split a textarea into a clean probe list: one attribute per line,
// trimmed, blanks and dupes dropped (order preserved).
export function parseProbes(text) {
    const seen = new Set();
    const out = [];
    for (const raw of String(text ?? "").split("\n")) {
        const t = raw.trim();
        if (!t || seen.has(t)) continue;
        seen.add(t);
        out.push(t);
    }
    return out;
}

// Parse an indented attribute list into a taxonomy: one probe per non-blank line, leading whitespace =
// nesting. A line's parent is the nearest line above it with strictly less indent; top-level lines get
// -1. Returns `{ probes, parent }` where `parent[i]` indexes into `probes` (-1 = top level) — the shape
// the worker/pipeline consume. No dedup (a repeated label can legitimately sit under two parents).
export function parseTaxonomy(text) {
    const probes = [];
    const parent = [];
    const stack = [];                                    // {indent, idx} of open ancestors, increasing indent
    for (const raw of String(text ?? "").split("\n")) {
        if (!raw.trim()) continue;
        const indent = raw.length - raw.trimStart().length;
        while (stack.length && stack[stack.length - 1].indent >= indent) stack.pop();
        const idx = probes.length;
        probes.push(raw.trim());
        parent.push(stack.length ? stack[stack.length - 1].idx : -1);
        stack.push({ indent, idx });
    }
    return { probes, parent };
}

function clampConfCut(c) {
    const n = Number(c);
    if (!Number.isFinite(n)) return DEFAULT_CONF_CUT;
    return Math.min(Math.max(n, CONF_CUT_MIN), CONF_CUT_MAX);
}

/**
 * Build the TriggerIloRebuildRequest body. Pure — no network — so the
 * enum mapping and the steering JSON are unit-testable.
 *
 * Only ever sets `algorithm` (+ `hyperparameters_json` for a steered
 * null_merge run). It never sets the legacy scalar fields (k, method,
 * epsilon, outlier_threshold), so the server's "hyperparameters_json is
 * mutually exclusive with fields 2-6" rule can't be tripped, and every
 * non-steered launch just takes the server-config defaults.
 */
export function buildRebuildRequest({ algorithm, probes = [], parent = [], confCut = DEFAULT_CONF_CUT }) {
    const meta = BY_VALUE.get(algorithm);
    if (!meta) throw new Error(`Unknown clustering algorithm: ${algorithm}`);

    const body = { algorithm: meta.proto };
    if (meta.steerable && probes.length > 0) {
        const hp = { probes, conf_cut: clampConfCut(confCut) };
        // Optional taxonomy DAG (-1 = top level). Only sent when it lines up with the probe list;
        // omitted for a flat probe set (the worker then treats every probe as a top-level domain).
        if (Array.isArray(parent) && parent.length === probes.length) {
            hp.parent = parent.map((p) => Number(p));
        }
        body.hyperparameters_json = JSON.stringify(hp);
    }
    return body;
}

/**
 * Launch a clustering pass. Resolves to the gateway response
 * ({ rebuildId } — a base64 generation id) or throws with the server's
 * human message on `err.serverMessage`.
 */
export function triggerIloRebuild(opts) {
    return restPost(REBUILD_PATH, buildRebuildRequest(opts));
}
